import React from "react";
import { Link } from "react-router-dom";
import Button from "../components/Button/Button";
import { Star } from "lucide-react"; 
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion"; // For animations
import AnimatedLightning from "./AnimatedLightning"; // Custom animated SVG component

// Type definition for individual stats item
type stat = {
    number: string;          
    label: string;           
    Icon: React.ReactNode;
    color: string;           
};

// Props for the HeroSection component
interface HeroSectionProps {
    title: string;        
    description: string; 
    stats: stat[];       
}

// HeroSection Component
const HeroSection: React.FC<HeroSectionProps> = ({ title, description, stats }) => {
    // Split title and description into words for word-by-word animation
    const titleWords = title.split(" ");
    const descriptionWords = description.split(" ");

    // Timing helpers for animation delays
    const titleDuration = titleWords.length * 0.05; // total duration for animating the title
    const descDuration = descriptionWords.length * 0.15; // total duration for animating description

    return (
        <section className="relative bg-gradient-to-br from-purple-200 via-blue-50 to-yellow-200 px-4 sm:px-6 md:px-8 py-8 md:py-16 lg:py-24">
            <div className="container mx-auto flex flex-col md:flex-row items-center justify-between px-2 sm:px-4 md:px-8 lg:px-16">
                
                {/* ---------- LEFT SIDE (Text Content) ---------- */}
                <div className="flex-1 mb-10 lg:mb-0 text-center lg:text-left">

                    {/* Badge/Tagline (small pill-shaped banner) */}
                    <motion.div
                        className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-full mb-4 shadow-sm mx-auto lg:mx-0"
                        initial={{ opacity: 0, y: -10 }} // Start hidden and slightly up
                        animate={{ opacity: 1, y: 0 }}   // Fade in and drop down
                        transition={{ duration: 0.6 }}  // Animation lasts 0.6 seconds (controls speed)
                    >
                        <Star size={18} className="text-white" />
                        <span className="text-sm font-medium">Recognition Platform</span>
                    </motion.div>

                    {/* Animated Title (word by word) */}
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500 mb-4">
                        {titleWords.map((word, idx) => (
                            <motion.span
                                key={idx}
                                className="inline-block mr-2"
                                initial={{ opacity: 0, y: 20 }} // Each word starts faded & down
                                animate={{ opacity: 1, y: 0 }}   // Animate up and fade in
                                transition={{
                                    delay: idx * 0.05, // Stagger animation by index
                                    duration: 0.6,
                                }}
                            >
                                {word}
                            </motion.span>
                        ))}
                    </h1>

                    {/* Decorative underline divider */}
                    <motion.div
                        className="w-40 h-1 bg-gradient-to-r from-yellow-400 to-orange-500 mb-6 mx-auto lg:mx-0"
                        initial={{ scaleX: 0 }} // Start collapsed
                        animate={{ scaleX: 1 }} // Expand to full width
                        transition={{
                            delay: titleDuration + 0.3,  // Wait until the title animation finishes, then pause 0.3s
                            duration: 0.5                // Take 0.5s to fully expand the line
                        }}
                    />

                    {/* Animated Description (word by word) */}
                    <p className="text-base md:text-lg text-black mb-6 md:mb-8">
                        {descriptionWords.map((word, idx) => (
                            <motion.span
                                key={idx}
                                className="inline-block mr-1"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    delay: titleDuration + idx * 0.15, // Animate after title
                                    duration: 0.3,
                                }}
                            >
                                {word}
                            </motion.span>
                        ))}
                    </p>

                    {/* CTA Button (with arrow icon) */}
                    <motion.div
                        className="flex justify-center lg:justify-start gap-4 mb-6 md:mb-8"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                            delay: titleDuration + descDuration + 0.8,
                            duration: 0.6,
                            type: "spring",
                        }}
                    >
                        <Link to="/auth">
                            <Button primary>
                                Get Started Today
                                <ArrowRight className="w-5 h-5 animate-arrow-slide" />
                            </Button>
                        </Link>
                    </motion.div>

                    {/* Stats Section */}
                    <motion.div
                        className="grid grid-cols-3 gap-4 md:gap-8 text-center"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            delay: titleDuration + descDuration + 1.4,
                            duration: 0.8,
                        }}
                    >
                        {stats.map((stat, idx) => (
                            <div key={idx} className="flex flex-col items-center">
                                {/* Icon inside colored circle */}
                                <div
                                    className={`w-14 h-14 flex items-center justify-center rounded-full mb-3 ${stat.color}`}
                                >
                                    {stat.Icon}
                                </div>
                                <h3 className="text-xl md:text-3xl font-bold">{stat.number}</h3>
                                <p className="text-xs md:text-base text-gray-500">{stat.label}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* ---------- RIGHT SIDE (Hero Illustration / Animation) ---------- */}
                <motion.div
                    className="flex-1 flex justify-center"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                        delay: titleDuration + descDuration + 1.6,
                        duration: 0.8,
                        type: "spring",
                    }}
                >
                    <AnimatedLightning /> {/* Custom animated SVG element */}
                </motion.div>
            </div>
        </section>
    );
};

export default HeroSection; 
